import type {ImageItem} from '@PosterWhiteboard/items/image-item/image-item.class';
import type {GA4EventSources} from '@Libraries/ga-events';
import type {CutoutRefineTool} from '@PosterWhiteboard/classes/item-refinement.types';

export type CutoutRefineTargetItem = ImageItem;

/** Everything the Refine cutout panel shows, republished by PosterCutoutRefine on every change. */
export interface CutoutRefineModeSnapshot {
  tool: CutoutRefineTool;
  /** Brush diameter in screen pixels */
  brushSize: number;
  showOriginal: boolean;
  /** There is something for "Reset all edits" to undo: painted strokes, or a removed background. */
  canResetAllEdits: boolean;
  canUndo: boolean;
  canRedo: boolean;
  isApplying: boolean;
}

/** Which control opened the mode; the panel is titled after it. Both open the same erase/restore tools. */
export enum CutoutRefineEntryPoint {
  ERASER = 'eraser',
  BACKGROUND_REMOVER = 'background-remover',
}

export interface CutoutRefineOpenRequest {
  item: CutoutRefineTargetItem;
  entryPoint: CutoutRefineEntryPoint;
  /** The editor surface the tile was clicked on, reported with the opened event. */
  gaSource: GA4EventSources;
}

/** How the user left the mode with their edits; reported with the applied event. */
export enum CutoutRefineExit {
  APPLY_BUTTON = 'apply_button',
  BACK = 'back',
  OUTSIDE_CLICK = 'outside_click',
  ESCAPE = 'escape',
  ENTER = 'enter',
  /** Another item was selected, from the layers panel. */
  SELECTION_CHANGE = 'selection_change',
}

export const CUTOUT_REFINE_GA_TYPE_BY_ENTRY_POINT: Readonly<Record<CutoutRefineEntryPoint, string>> = {
  [CutoutRefineEntryPoint.ERASER]: 'eraser',
  [CutoutRefineEntryPoint.BACKGROUND_REMOVER]: 'background_remover',
};
export const CUTOUT_REFINE_GA_TOOLS_BOTH = 'both';
export const CUTOUT_REFINE_GA_TOOLS_NONE = 'none';

/** A session that is downloading its images, and the control that opened it. */
export interface CutoutRefineLoading {
  itemId: string;
  entryPoint: CutoutRefineEntryPoint;
}

export interface CutoutRefineModeEntry extends CutoutRefineModeSnapshot {
  itemId: string;
  entryPoint: CutoutRefineEntryPoint;
}

export const CUTOUT_REFINE_MIN_BRUSH_SIZE = 4;
export const CUTOUT_REFINE_MAX_BRUSH_SIZE = 100;
export const CUTOUT_REFINE_DEFAULT_BRUSH_SIZE = 40;
export const CUTOUT_REFINE_ORIGINAL_PREVIEW_OPACITY = 0.35;
/** Each undo step keeps the strokes as they were and Undo replays them onto the masks; the depth is capped to bound that replay. */
export const CUTOUT_REFINE_MAX_UNDO_STEPS = 20;
/** Ends the file name of an image flattened from a refinement too big to keep as strokes. */
export const CUTOUT_REFINE_FLATTENED_FILE_SUFFIX = '-erased.png';

/**
 * Mirrors the `transparency-checkerboard` mixin (public/assets/css/components/core/_background.scss) used for
 * the timeline's transparent page background: $neutral-0 white with 8px black squares at 5% opacity.
 */
export const CUTOUT_REFINE_CHECKER_SQUARE_SCREEN_PX = 8;
export const CUTOUT_REFINE_CHECKER_LIGHT = '#FFFFFF';
export const CUTOUT_REFINE_CHECKER_DARK = 'rgba(0, 0, 0, 0.05)';
/** A white ring outlined in near-black on both edges, so the brush reads over light and dark areas alike. */
export const CUTOUT_REFINE_BRUSH_CURSOR_RING_COLOR = '#FFFFFF';
export const CUTOUT_REFINE_BRUSH_CURSOR_RING_PX = 2;
export const CUTOUT_REFINE_BRUSH_CURSOR_OUTLINE_COLOR = '#141325';
export const CUTOUT_REFINE_BRUSH_CURSOR_OUTLINE_PX = 0.5;
/** Above the pages and their item controls inside the whiteboard (z-index 2 at most), below the floating rail and side panel. */
export const CUTOUT_REFINE_BRUSH_CURSOR_Z_INDEX = '3';

/** Marks Refine cutout UI (side panel, mobile tool bar, brush size sheet, undo/redo, layers and zoom controls, layers panel) so a press there does not leave the mode. */
export const CUTOUT_REFINE_SURFACE_DATA_KEY = 'cutout-refine-surface';
export const CUTOUT_REFINE_SURFACE_ATTRIBUTE = `data-${CUTOUT_REFINE_SURFACE_DATA_KEY}`;
/** A press that travels further than this before release is a drag, not a click that leaves the mode. */
export const CUTOUT_REFINE_OUTSIDE_CLICK_TOLERANCE_PX = 4;
